import * as Sentry from '@sentry/nextjs';
import axios from 'data/axios';
import { NextSeo } from 'next-seo';
import React, { useState } from 'react';

import Footer from '../components/Footer';
import PoiSuggestionForm from '../components/forms/PoiSuggestionForm';
import LoadingProgressCircle from '../components/LoadingProgressCircle';
import PoiSuggestionError from '../components/suggest-poi/PoiSuggestionError';
import PoiSuggestionSuccess from '../components/suggest-poi/PoiSuggestionSuccess';
import pages from '../config/pages';
import { Mixpanel } from 'utils/Mixpanel';
import useGTM from 'hooks/useGTM';
import { AnalyticsEvent } from 'models/Analytics';
import useUser from 'hooks/useUser';
import Login from 'components/auth/Login';

function PoiSuggestionPage() {
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState(false);
    const [hasPhotos, setHasPhotos] = useState(false);
    const { sendEvent } = useGTM();
    const { user } = useUser();

    const handleReset = () => {
        setUploadProgress(0);
        setUploadSuccess(false);
        setUploadError(false);
        setHasPhotos(false);
    };

    const handleUploadProgress = (percentage) => {
        setUploadProgress(percentage);
    };

    const handleSubmit = async (values, files) => {
        const form = new FormData();

        setHasPhotos(files.length > 0);

        Mixpanel.track('Poi suggestion form submitted', {
            hasPhotos: files.length > 0
        });

        for (const key in values) {
            if (Object.hasOwnProperty.call(values, key)) {
                if (key === 'location') {
                    form.append('location[lat]', values[key].lat);
                    form.append('location[lng]', values[key].lng);
                } else {
                    form.append(key, values[key]);
                }
            }
        }

        for (let i = 0; i < files.length; i++) {
            form.append('files[]', files[i]);
        }

        try {
            await axios.post('/poi-suggestion', form, {
                onUploadProgress: (progressEvent) =>
                    handleUploadProgress((progressEvent.loaded / progressEvent.total) * 100)
            });

            setUploadSuccess(true);

            sendEvent({ action: AnalyticsEvent.PHOTOS_UPLOADED });
        } catch (error) {
            // Send error event?
            Mixpanel.track('Poi suggestion form error', {
                hasPhotos: files.length > 0
            });
            setUploadError(true);
            sendEvent({ action: AnalyticsEvent.PHOTO_UPLOAD_ERROR });
            Sentry.captureException(error);
        }
    };

    return (
        <div className="single-page predlagaj-idejo pt-8">
            <NextSeo title={pages.suggest.title} description={pages.suggest.seo.description} />

            {!user && (
                <div className="md:pt-8 pb-24 max-w-lg mx-auto">
                    <Login title="Preden lahko predlagaš izletno idejo se moraš prijaviti" />
                </div>
            )}
            {user && (
                <section id="poi-suggestion-form" className="container pb-28">
                    {uploadProgress === 0 && <PoiSuggestionForm onSubmit={handleSubmit} />}
                    {uploadProgress > 0 && !(uploadSuccess || uploadError) && (
                        <div>
                            <div className="mb-12 text-center">
                                <h1 className="text-brand">Podatki se pošiljajo ...</h1>
                                <LoadingProgressCircle percentage={uploadProgress} />
                            </div>
                        </div>
                    )}
                    {uploadSuccess && <PoiSuggestionSuccess hasPhotos={hasPhotos} onReset={handleReset} />}
                    {uploadError && <PoiSuggestionError />}
                </section>
            )}

            <Footer />
        </div>
    );
}

export default PoiSuggestionPage;
